import { Sparkles, Camera, FileSpreadsheet, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

const HERO =
  "https://images.unsplash.com/photo-1592228533283-d78f7c1cf453?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwyfHx3YXJlaG91c2UlMjBpbnZlbnRvcnklMjB3b3JrZXIlMjBzY2FubmluZyUyMGJhcmNvZGUlMjB0YWJsZXR8ZW58MHx8fHwxNzkxNDE4MTU4fDA&ixlib=rb-4.1.0&q=85";

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5">
    <path fill="#EA4335" d="M12 10.2v3.9h5.5c-.24 1.4-1.7 4.1-5.5 4.1-3.3 0-6-2.7-6-6s2.7-6 6-6c1.9 0 3.1.8 3.8 1.5l2.6-2.5C16.9 2.9 14.7 2 12 2 6.9 2 2.8 6.1 2.8 11.2S6.9 20.4 12 20.4c5.9 0 9.8-4.1 9.8-9.9 0-.7-.1-1.2-.2-1.7H12z" />
  </svg>
);

const features = [
  { icon: Camera, label: "Snap or batch-upload photos" },
  { icon: Sparkles, label: "AI extracts names, text & categories" },
  { icon: FileSpreadsheet, label: "Live spreadsheet + CSV export" },
];

export default function Login() {
  const { login } = useAuth();

  return (
    <div className="min-h-screen grid-texture grid lg:grid-cols-2">
      {/* Left — brand / value */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 overflow-hidden">
        <img src={HERO} alt="warehouse" className="absolute inset-0 h-full w-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-br from-background via-background/90 to-background/70" />
        <div className="relative z-10 flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center overflow-hidden rounded-xl ring-1 ring-primary/30">
            <img src="/logo.png" alt="SmartScan" className="h-full w-full object-cover" />
          </div>
          <span className="font-heading text-xl font-extrabold text-slate-50">
            SmartScan<span className="text-primary"> Inventory</span>
          </span>
        </div>
        <div className="relative z-10 max-w-md">
          <h1 className="font-heading text-4xl font-extrabold tracking-tight text-slate-50 leading-tight">
            Scan it. Name it. <span className="text-primary">Track it.</span>
          </h1>
          <p className="mt-4 text-slate-300 leading-relaxed">
            AI-powered inventory for warehouses and field teams. Point your camera, let the AI do the cataloguing, export a clean sheet in seconds.
          </p>
          <ul className="mt-8 space-y-3">
            {features.map((f) => (
              <li key={f.label} className="flex items-center gap-3 text-sm text-slate-200">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-accent/15 text-accent">
                  <f.icon className="h-4 w-4" />
                </span>
                {f.label}
              </li>
            ))}
          </ul>
        </div>
        <div className="relative z-10 text-xs text-muted-foreground">
          © {new Date().getFullYear()} SmartScan Inventory
        </div>
      </div>

      {/* Right — sign in */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm fade-up">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-xl ring-1 ring-primary/30">
              <img src="/logo.png" alt="SmartScan" className="h-full w-full object-cover" />
            </div>
            <span className="font-heading text-lg font-extrabold text-slate-50">
              SmartScan<span className="text-primary"> Inventory</span>
            </span>
          </div>

          <div className="rounded-2xl border border-border bg-card p-8">
            <div className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-[11px] font-medium text-primary mb-5">
              <Zap className="h-3 w-3" /> AI Inventory Workspace
            </div>
            <h2 className="font-heading text-2xl font-bold text-slate-50">Welcome</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Sign in to access your inventory, scanning and exports.
            </p>

            <Button
              data-testid="google-login-button"
              onClick={login}
              className="mt-6 w-full gap-3 bg-white text-slate-800 hover:bg-slate-100 font-semibold h-12 transition-transform active:scale-[0.98]"
            >
              <GoogleIcon /> Continue with Google
            </Button>

            <p className="mt-5 text-center text-[11px] text-muted-foreground leading-relaxed">
              Your inventory is private to your account. We never post anything on your behalf.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
